import { z } from "zod";
import { analyticsBoundBy } from "./analyticsEnums";

/**
 * One input of the bound workflow, wired to something in Midship.
 *
 * Discriminated on `source`, so an unknown source fails to parse. Within a known
 * source, zod's default strip policy applies: a stray key (a `documentId` on a
 * resolved input) is dropped at the parse boundary rather than stored. That is
 * deliberate - `.strict()` would reject the payload the moment the backend adds
 * a field, and stripping already keeps an impossible pairing from persisting.
 *
 * Only a `document` binding stores an identifier. The other two store none, and
 * that is the point: each names a rule for finding a file rather than a file, and
 * a stored id would be silently wrong the moment the control is cloned. For
 * `sample_export` the file is whichever one the test table marks as its analytics
 * input, which is a state on the document's row on the session. For `sample_row`
 * it is the sample's own row of the population, and the sample is whichever one
 * the scope being resolved is for — an id here would name a sample the run may
 * not be testing.
 */
export const DocumentInputBindingSchema = z.object({
  source: z.literal("document"),
  documentId: z.string(),
  boundBy: analyticsBoundBy,
  boundAt: z.string(),
  // Carried from the agent prefill a human correction overrode, so the
  // disagreement stays auditable rather than being erased by the correction.
  confidence: z.number().nullable().default(null),
});

export const SampleExportInputBindingSchema = z.object({
  source: z.literal("sample_export"),
  boundBy: analyticsBoundBy,
  boundAt: z.string(),
});

/**
 * The sample's own row of the population, delivered as a one-row file.
 *
 * The only class whose content differs per sample: the other two hand the same
 * file to every sample of the column, so a workflow bound only to those is never
 * told which sample it is testing.
 */
export const SampleRowInputBindingSchema = z.object({
  source: z.literal("sample_row"),
  boundBy: analyticsBoundBy,
  boundAt: z.string(),
});

export const InputBindingSchema = z.discriminatedUnion("source", [
  DocumentInputBindingSchema,
  SampleExportInputBindingSchema,
  SampleRowInputBindingSchema,
]);

export type InputBinding = z.infer<typeof InputBindingSchema>;

/**
 * The link between an analytics column and the published workflow that runs it.
 *
 * Every id here is one of the workflow engine's integer REST ids, never a
 * Cascade external id: two identifier mappings exist mid-migration and only
 * these reach Midship.
 *
 * It carries nothing about outputs — what a run produced lives on the run
 * manifest — and nothing about the parameter list, which is re-projected from
 * the workflow's activated version on every read so it cannot drift from the
 * workflow.
 *
 * It deliberately carries no version. Midship creates an unpinned workflow
 * instance, so each run follows whichever version Optro Analytics has activated;
 * the run manifest records the version that actually executed.
 */
export const WorkflowBindingSchema = z.object({
  automationWorkflowId: z.number(),
  automationWorkflowInstanceId: z.number().nullable().default(null),
  /**
   * Null when the binding names a workflow that already exists: the category
   * belongs to that workflow in Optro Analytics, and the backend model
   * (`workflow_binding.py`) only records one on the path that creates a
   * workflow, where the create call requires it. Nullable here for the same
   * reason `attachedAt` is declared: a strict parse of a field the server may
   * legitimately serve as null fails the whole control payload, one row
   * breaking the entire page rather than one column falling back.
   */
  workflowCategoryId: z.number().nullable().default(null),
  inputBindings: z.record(InputBindingSchema).default({}),
  /**
   * Stamped by the backend (MS-3091) on a successful attach.
   *
   * Declared here for persistence, not for display: nothing on the frontend
   * reads this value. This schema is a non-strict `z.object`, so a key it does
   * not declare is silently **stripped on parse**. Declaring it is what keeps a
   * parsed binding holding exactly what the backend sent instead of dropping
   * the stamp at the parse boundary, so local state and the staged update queue
   * carry the same binding the server does. Column-update PUTs cannot zero it
   * either way: `stripWorkflowBindingForUpdate` nulls the binding on every one
   * of them.
   *
   * `.optional()` and deliberately **not** `.default(null)`: a default would
   * materialise the key on every outbound payload, including for bindings the
   * backend never sent one for. The create payload is the one payload class
   * that still carries a binding, and it is built field by field against this
   * type, so a default would force it to name `attachedAt`. The backend
   * `WorkflowBinding` model is `extra="forbid"`, so an `attachedAt` it does not
   * yet declare makes every create carrying a binding fail with a 422 and makes
   * the deploy order of the two halves matter. Optional keeps both directions
   * honest: a value the backend sent round-trips untouched, and one it never
   * sent is not invented.
   *
   * `z.string()`, not a coerced date: the value is carried through verbatim so
   * a round trip cannot re-format it.
   */
  attachedAt: z.string().nullable().optional(),
  /**
   * Stamped by the backend when `configure` mints a workflow shell, and by
   * nothing else: attach composes its binding fresh and a clone clears it.
   * Non-null with a null instance id therefore means a shell built from
   * Midship, which is the state the replace confirmation warns on. This is the
   * server-side provenance that lets that warning survive a reload, where the
   * session-scoped record dies.
   *
   * Same declaration shape as `attachedAt`, for the same reasons: declared so
   * the parse does not strip it, `.optional()` rather than `.default(null)` so
   * the create payload never invents it against a backend that is
   * `extra="forbid"`, and a verbatim string so a round trip cannot re-format
   * it.
   */
  builtAt: z.string().nullable().optional(),
});

export type WorkflowBinding = z.infer<typeof WorkflowBindingSchema>;

/**
 * Analytics Column Configuration
 *
 * The criteria half mirrors `AttributeTestColumnSchema` field for field, because
 * the two column kinds are authored identically and differ only in who reaches a
 * verdict — an LLM there, a published workflow here.
 *
 * There is deliberately no `attributeId`: the column envelope
 * (`ProcedureColumn.attributeId`) already carries one, and a second copy on the
 * subtype would be a second path to the same fact.
 *
 * `workflowBinding` is nullable — the column exists in the authoring UI before
 * any workflow is attached, which is exactly the `not_configured` state.
 */
export const AnalyticsColumnSchema = z.object({
  id: z.string(),
  name: z.string().nullable().optional(),
  passCriteria: z.string(),
  failCriteria: z.string().nullable(),
  naCriteria: z.string().nullable(),
  procedureGuidance: z.string().nullable(),
  tolerances: z.string().nullable(),
  expectedEvidences: z.string().nullable(),
  workflowName: z.string().nullable(),
  draftedTest: z.string().nullable(),
  workflowBinding: WorkflowBindingSchema.nullable(),
  /**
   * The workflow category the AI plan suggested when it proposed this column,
   * drawn from those its author could create workflows in. Only a default for
   * the Edit Column dialog; a built workflow's category lives on the binding.
   *
   * `.optional()` rather than `.default(null)` so the create payload, built
   * field by field, need not name a value only the backend ever sets.
   */
  suggestedCategoryId: z.number().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type AnalyticsColumn = z.infer<typeof AnalyticsColumnSchema>;
